import React from "react";
import {
  Button,
  Dropdown,
  Image,
  Input,
  Modal,
  Space,
  Typography,
  message,
} from "antd";
import { MoreOutlined } from "@ant-design/icons";
import {
  deleteDiscussionMessage,
  editDiscussionMessage,
} from "../helper/discussions";

// Shared edit / soft-delete logic + rendering for a single discussion message,
// used by BOTH DiscussionDrawer and the propertyTasks task-thread drawer so the
// two message renderers can't drift apart. `threadId` is the discussion id.
//
// Editing is text-only (a message's images are preserved untouched on save); a
// soft-delete sets `deleted` + blanks the content. Both go through the deployed
// rules, which allow only the author to mutate text/imageUrls/editedAt/deleted.
export const useMessageEdit = (threadId) => {
  const [editingId, setEditingId] = React.useState(null);
  const [editText, setEditText] = React.useState("");
  const [savingEdit, setSavingEdit] = React.useState(false);

  // Clear any in-progress edit — call this when the host drawer closes / switches
  // threads so a stale draft (or a stuck saving spinner) can't leak across.
  const reset = React.useCallback(() => {
    setEditingId(null);
    setEditText("");
    setSavingEdit(false);
  }, []);
  const beginEdit = (n) => {
    setEditingId(n._id);
    setEditText(n.text || "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEdit = async (n) => {
    const body = (editText || "").trim();
    if (!threadId || !n?._id) return;
    if (!body) {
      message.error("Message can't be empty — delete it instead");
      return;
    }
    setSavingEdit(true);
    try {
      await editDiscussionMessage(threadId, n._id, {
        text: body,
        imageUrls: n.imageUrls || [],
      });
      cancelEdit();
    } catch (e) {
      console.error("edit message failed", e);
      message.error(e?.message || "Couldn't edit");
    } finally {
      setSavingEdit(false);
    }
  };
  const confirmDelete = (n) => {
    if (!threadId || !n?._id) return;
    Modal.confirm({
      title: "Delete message?",
      content: "This can't be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          if (editingId === n._id) cancelEdit();
          await deleteDiscussionMessage(threadId, n._id);
        } catch (e) {
          console.error("delete message failed", e);
          message.error(e?.message || "Couldn't delete");
          throw e; // reject so antd keeps the dialog open + shows its spinner
        }
      },
    });
  };

  return {
    editingId,
    editText,
    setEditText,
    savingEdit,
    beginEdit,
    cancelEdit,
    saveEdit,
    confirmDelete,
    reset,
  };
};

// The own-message "⋯" menu (Edit / Delete) — rendered only for the author's own,
// non-deleted messages. `style` lets the caller push it to the row's right edge.
export const MessageActionsMenu = ({ n, myUid, onEdit, onDelete, style }) =>
  n.authorUid === myUid && !n.deleted ? (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: [
          { key: "edit", label: "Edit", onClick: () => onEdit(n) },
          { type: "divider" },
          { key: "delete", label: "Delete", danger: true, onClick: () => onDelete(n) },
        ],
      }}
    >
      <Button type="text" size="small" icon={<MoreOutlined />} style={style} />
    </Dropdown>
  ) : null;

// The body of a message bubble: a "deleted" placeholder, the inline edit composer
// (Enter saves, Shift+Enter newlines), or the normal text + image grid.
export const MessageBody = ({
  n,
  editing,
  editText,
  setEditText,
  savingEdit,
  onSave,
  onCancel,
}) => {
  if (n.deleted) {
    return (
      <Typography.Text type="secondary" italic>
        This message was deleted
      </Typography.Text>
    );
  }
  if (editing) {
    return (
      <div style={{ marginTop: 4 }}>
        <Input.TextArea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          autoSize={{ minRows: 1, maxRows: 6 }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              onSave(n);
            }
          }}
        />
        <Space size={6} style={{ marginTop: 6 }}>
          <Button
            size="small"
            type="primary"
            loading={savingEdit}
            onClick={() => onSave(n)}
          >
            Save
          </Button>
          <Button size="small" onClick={onCancel} disabled={savingEdit}>
            Cancel
          </Button>
        </Space>
      </div>
    );
  }
  return (
    <>
      {n.text ? <div style={{ whiteSpace: "pre-wrap" }}>{n.text}</div> : null}
      {Array.isArray(n.imageUrls) && n.imageUrls.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <Image.PreviewGroup>
            <Space wrap size={6}>
              {n.imageUrls.map((url, i) => (
                <Image
                  key={i}
                  src={url}
                  width={72}
                  height={72}
                  style={{ objectFit: "cover", borderRadius: 4 }}
                />
              ))}
            </Space>
          </Image.PreviewGroup>
        </div>
      )}
    </>
  );
};
